
import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import { useTranslation } from "react-i18next";

import {
  HiOutlineBookOpen,
  HiOutlineCalendarDays,
  HiOutlineArrowDownTray,
  HiOutlineDocumentText,
} from "react-icons/hi2";

import { media } from "../../server";

/* ============================================
   MEDIA URL HELPER
============================================ */

function getMediaUrl(value) {
  if (!value || typeof value !== "string") {
    return null;
  }

  const path = value.trim().replace(/\\/g, "/");

  if (!path) return null;

  if (/^https?:\/\//i.test(path)) {
    return path;
  }

  // Reject unsupported URL schemes.
  if (/^[a-z][a-z0-9+.-]*:/i.test(path)) {
    return null;
  }

  const base = String(media || "").replace(/\/+$/, "");
  const filename = path.replace(/^\/+/, "");

  return base
    ? `${base}/${filename}`
    : `/${filename}`;
}

/* ============================================
   IMAGE CANDIDATES
============================================ */

function getBookImageSources(image) {
  if (!image || typeof image !== "string") {
    return [];
  }

  const original = image.trim().replace(/\\/g, "/");

  if (!original) return [];

  // Absolute URLs
  if (/^https?:\/\//i.test(original)) {
    return [original];
  }

  if (/^[a-z][a-z0-9+.-]*:/i.test(original)) {
    return [];
  }

  const path = original.replace(/^\/+/, "");

  // Try WebP first for older database records.
  const webpPath = path.replace(
    /\.(png|jpe?g)$/i,
    ".webp"
  );

  const paths = [...new Set([webpPath, path])];

  const sources = paths.flatMap((item) => {
    const filename = item.split("/").pop();

    return [
      getMediaUrl(item),
      `/images/${filename}`,
      `/${item}`,
    ];
  });

  return [...new Set(sources.filter(Boolean))];
}

/* ============================================
   BOOK COVER
============================================ */

function BookCover({ image, title }) {
  const sources = useMemo(
    () => getBookImageSources(image),
    [image]
  );

  const [sourceIndex, setSourceIndex] = useState(0);

  useEffect(() => {
    setSourceIndex(0);
  }, [image]);

  const currentSource = sources[sourceIndex];

  return (
    <div
      className="
        relative h-full w-full
        overflow-hidden

        rounded-r-lg rounded-l-[3px]
        border border-black/10

        bg-[#19344D]

        shadow-[12px_20px_35px_rgba(11,23,38,0.20)]

        transition-all duration-500

        group-hover:-translate-y-2
        group-hover:shadow-[16px_28px_45px_rgba(11,23,38,0.22)]
      "
    >
      {currentSource ? (
        <img
          key={currentSource}
          src={currentSource}
          alt={title}
          loading="lazy"
          decoding="async"
          onError={() =>
            setSourceIndex((prev) => prev + 1)
          }
          className="
            block h-full w-full
            object-contain
          "
        />
      ) : (
        <div
          className="
            relative flex h-full w-full
            flex-col items-center justify-center
            gap-6 overflow-hidden

            bg-gradient-to-br
            from-[#294961]
            via-[#19344D]
            to-[#0B1726]

            px-5 text-center
          "
        >
          {/* Decorative cover detail */}
          <div
            aria-hidden="true"
            className="
              absolute inset-4
              border border-[#D7B77F]/25
            "
          />

          <HiOutlineBookOpen
            size={54}
            aria-hidden="true"
            className="relative text-[#D7B77F]"
          />

          <div className="relative h-px w-14 bg-[#D7B77F]/65" />

          <p
            className="
              relative font-cairo
              text-sm font-semibold
              leading-7 text-white
            "
          >
            {title}
          </p>
        </div>
      )}

      {/* Book spine effect */}
      <div
        aria-hidden="true"
        className="
          pointer-events-none
          absolute inset-y-0 start-0

          w-[9px]
          border-e border-black/10

          bg-gradient-to-r
          from-black/20
          via-white/10
          to-transparent
        "
      />

      {/* Book edge highlight */}
      <div
        aria-hidden="true"
        className="
          pointer-events-none
          absolute inset-y-0 end-0
          w-px bg-white/25
        "
      />
    </div>
  );
}

/* ============================================
   BOOKS CARD
============================================ */

function BooksCard({
  book,
  index = 0,
}) {
  const { i18n } = useTranslation();

  const isEnglish = (
    i18n.resolvedLanguage ||
    i18n.language ||
    "ar"
  )
    .toLowerCase()
    .startsWith("en");

  const title = isEnglish
    ? book?.titleEn || book?.titleAr
    : book?.titleAr || book?.titleEn;

  const fileUrl = getMediaUrl(book?.file);

  const content = {
    eyebrow: isEnglish
      ? "LEGAL PUBLICATION"
      : "إصدار قانوني",

    download: isEnglish
      ? "Download Book"
      : "تحميل الكتاب",

    unavailable: isEnglish
      ? "Download Unavailable"
      : "التحميل غير متاح",
  };

  const number = String(index + 1).padStart(2, "0");

  return (
    <article
      className="
        group flex h-full min-w-0
        flex-col overflow-hidden

        rounded-[22px]

        border border-[#D9E4EC]
        bg-white

        shadow-[0_10px_35px_rgba(15,39,55,0.05)]

        transition-all duration-300

        hover:border-brand-gold/40
        hover:shadow-card

        dark:border-white/10
        dark:bg-[#1B3549]
        dark:shadow-none
        dark:hover:border-brand-gold/35
      "
    >
      {/* ======================================
          BOOK COVER AREA
      ====================================== */}

      <div
        className="
          relative flex h-[340px]
          items-center justify-center
          overflow-hidden

          border-b border-[#D9E4EC]

          bg-[#F3F1EB]

          px-7 py-8

          dark:border-white/10
          dark:bg-[#11283A]

          sm:h-[370px]
        "
      >
        {/* Subtle geometric background */}
        <div
          aria-hidden="true"
          className="
            pointer-events-none
            absolute -end-20 -top-24

            h-60 w-60 rounded-full
            border border-brand-gold/10
          "
        />

        <div
          aria-hidden="true"
          className="
            pointer-events-none
            absolute -start-24 -bottom-20

            h-56 w-56 rounded-full
            border border-brand-gold/10
          "
        />

        {/* Book number */}
        <span
          className="
            absolute end-5 top-5

            rounded-lg
            border border-[#D9E4EC]
            bg-white/80

            px-3 py-1.5

            font-cairo text-xs
            font-semibold tabular-nums

            text-[#19344D]

            dark:border-white/10
            dark:bg-white/10
            dark:text-white
          "
        >
          {number}
        </span>

        {/* Book image */}
        <div
          className="
            relative aspect-[3/4]
            h-full max-h-[300px]

            sm:max-h-[320px]
          "
        >
          <BookCover
            image={book?.Image}
            title={title}
          />
        </div>

      </div>

      {/* ======================================
          CARD CONTENT
      ====================================== */}

      <div
        className="
          flex flex-1 flex-col

          px-6 pb-6 pt-7
          sm:px-7 sm:pb-7
        "
      >
        {/* Eyebrow */}
        <div className="mb-5 flex items-center gap-3">

          <HiOutlineDocumentText
            size={19}
            aria-hidden="true"
            className="text-skin-accent"
          />

          <span className="eyebrow text-skin-accent">
            {content.eyebrow}
          </span>
        </div>

        {/* Book title */}
        <h3
          className="
            card-title
            text-skin-heading

            transition-colors duration-300

            group-hover:text-brand-goldDark
            dark:group-hover:text-brand-goldLight
          "
        >
          {title}
        </h3>

        {/* Publication date */}
        {book?.date && (
          <div
            className="
              mt-5 flex items-center gap-2

              text-skin-muted
            "
          >
            <HiOutlineCalendarDays
              size={18}
              aria-hidden="true"
            />

            <span className="body-3">
              {book.date}
            </span>
          </div>
        )}

        {/* ======================================
            DOWNLOAD AREA
        ====================================== */}

        <div className="mt-auto pt-8">

          <div className="mb-6 h-px bg-skin-border" />

          {fileUrl ? (
            <a
              href={fileUrl}
              download
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`${content.download}: ${title}`}
              className="
                group/button flex
                min-h-[52px] w-full

                items-center justify-center
                gap-3

                rounded-xl
                border border-[#19344D]
                bg-[#19344D]

                px-5 py-3
                text-white

                transition-all duration-300

                hover:border-[#294961]
                hover:bg-[#294961]

                focus-visible:outline-none
                focus-visible:ring-2
                focus-visible:ring-brand-gold

                dark:border-white/20
                dark:bg-white/10
                dark:backdrop-blur-xl
                dark:hover:bg-white/20
              "
            >
              <HiOutlineArrowDownTray
                size={20}
                aria-hidden="true"
                className="
                  shrink-0
                  transition-transform duration-300
                  group-hover/button:translate-y-1
                "
              />

              <span className="button-sm">
                {content.download}
              </span>
            </a>
          ) : (
            <div
              className="
                flex min-h-[52px]
                items-center justify-center
                gap-3

                rounded-xl
                border border-skin-border
                bg-skin-soft

                px-5 py-3
                text-skin-muted
              "
            >
              <HiOutlineArrowDownTray
                size={19}
                aria-hidden="true"
              />

              <span className="button-sm">
                {content.unavailable}
              </span>
            </div>
          )}
        </div>

      </div>
    </article>
  );
}

export default BooksCard;
